import React from 'react';
import { Terminal, ArrowLeft } from 'lucide-react';

// Background with enhanced gradient and animated decorative elements
const Background = () => (
  <div className="absolute inset-0 z-0 overflow-hidden">
    {/* Main gradient overlay */}
    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900" />
    
    {/* Animated floating elements */}
    <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse" 
         style={{ animationDelay: '0s', animationDuration: '4s' }} />
    <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse" 
         style={{ animationDelay: '2s', animationDuration: '6s' }} />
    <div className="absolute top-1/2 right-1/3 w-32 h-32 bg-pink-500/10 rounded-full blur-2xl animate-pulse" 
         style={{ animationDelay: '1s', animationDuration: '5s' }} />
    
    {/* Grid pattern overlay */}
    <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />
  </div>
);

// Enhanced content with better spacing and visual hierarchy
const Content = () => (
  <main className="relative z-10 flex flex-col items-center justify-center text-center space-y-8" 
        role="main" aria-labelledby="not-found-heading">
    
    {/* Icon with enhanced styling */}
    <div className="flex justify-center mb-2">
      <div className="relative">
        <Terminal className="w-16 h-16 sm:w-20 sm:h-20 text-blue-400 drop-shadow-lg" aria-hidden="true" />
        <div className="absolute inset-0 bg-blue-400/20 rounded-lg blur-xl" />
      </div>
    </div>
    
    {/* 404 heading with enhanced gradient */}
    <h1
      id="not-found-heading"
      className="text-7xl sm:text-9xl lg:text-[12rem] font-mono font-bold leading-none 
                 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 
                 bg-clip-text text-transparent drop-shadow-2xl"
    >
      404
    </h1>
    
    {/* Subtitle with better typography */}
    <div className="space-y-4">
      <p className="text-xl sm:text-2xl lg:text-3xl text-gray-200 font-mono font-medium">
        <span className="text-blue-400 text-2xl sm:text-3xl lg:text-4xl">&lt;</span>
        <span className="mx-2">Page Not Found</span>
        <span className="text-blue-400 text-2xl sm:text-3xl lg:text-4xl">/&gt;</span>
      </p>
      
      <p className="text-base sm:text-lg text-gray-400 font-mono max-w-2xl mx-auto leading-relaxed px-4">
        The page you&apos;re looking for doesn&apos;t exist or has been moved to another location.
      </p>
    </div>
    
    {/* Enhanced button with better hover effects */}
    <div className="pt-4">
      <button
        onClick={() => window.history.back()}
        className="group relative overflow-hidden bg-gradient-to-r from-blue-500 to-purple-500 px-6 sm:px-8 py-3 rounded-lg font-mono font-semibold transition-all duration-300 hover:scale-105 w-full sm:w-auto"
        aria-label="Return to previous page"
      >
        <span className="relative z-10">Back to Home</span>
        
        {/* Animated background overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent 
                        transform -skew-x-12 -translate-x-full group-hover:translate-x-full 
                        transition-transform duration-700 ease-in-out" />
      </button>
    </div>
    
    {/* Additional decorative elements */}
    <div className="absolute -top-10 -left-10 w-20 h-20 border border-blue-500/20 rounded-full animate-spin" 
         style={{ animationDuration: '20s' }} />
    <div className="absolute -bottom-10 -right-10 w-16 h-16 border border-purple-500/20 rounded-full animate-spin" 
         style={{ animationDuration: '15s', animationDirection: 'reverse' }} />
  </main>
);

const NotFound404 = () => {
  return (
    <div className="min-h-screen w-full bg-gray-900 text-white flex items-center justify-center relative overflow-hidden">
      <div className="relative w-full h-screen flex items-center justify-center px-6 sm:px-8 lg:px-12">
        <Background />
        <Content />
      </div>
    </div>
  );
};

export default NotFound404;